/**
 * features/hr/PayslipSendMonitor.tsx
 * ------------------------------------------------------------------
 * HR PAYROLL — watching payslips go out, and seeing afterwards who got
 * theirs.
 *
 * Replaces the one-line "Emailing payslips… 12 of 42" message. Shows a
 * progress bar, counts (sent / failed / no email / waiting) that filter
 * the list, and every person's own result with the reason a send failed.
 * Failed ones can be retried on their own.
 *
 * Closing it does not stop the run: PayRun owns the sending loop and the
 * results, and the Pay Run table's Emailed column fills in either way
 * (it reads the `send` status the Cloud Function saves on each payslip).
 *
 * "Sent" means the school's mail server accepted the message. A bounce
 * that comes back later lands in the HR mailbox.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { X, RotateCcw, Download, Mail } from 'lucide-react';
import { modalBackdrop, modalSurface, btnPrimary, btnGhost } from '@/features/finance/ui';

export type SendState = 'queued' | 'sending' | 'sent' | 'failed' | 'noemail';

export interface SendItem {
  staffId: string;
  name: string;
  email?: string;
}

type Filter = 'all' | 'sent' | 'failed' | 'noemail' | 'waiting';

const ORDER: Record<SendState, number> = { failed: 0, noemail: 1, sending: 2, queued: 3, sent: 4 };

export default function PayslipSendMonitor({
  open, onClose, periodLabel, items, status, reasons, sentAt, running, onRetry,
}: {
  open: boolean;
  onClose: () => void;
  periodLabel: string;
  items: SendItem[];
  status: Record<string, SendState>;
  reasons: Record<string, string>;
  sentAt: Record<string, string>;
  running: boolean;
  onRetry: (staffIds: string[]) => void;
}) {
  const [filter, setFilter] = useState<Filter>('all');
  useEffect(() => { if (open) setFilter('all'); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const counts = useMemo(() => {
    const c = { sent: 0, failed: 0, noemail: 0, waiting: 0 };
    for (const it of items) {
      const s = status[it.staffId];
      if (s === 'sent') c.sent++; else if (s === 'failed') c.failed++; else if (s === 'noemail') c.noemail++; else c.waiting++;
    }
    return c;
  }, [items, status]);

  const rows = useMemo(() => items
    .filter((it) => {
      const s = status[it.staffId];
      if (filter === 'all') return true;
      if (filter === 'waiting') return s === 'queued' || s === 'sending';
      return s === filter;
    })
    .sort((a, b) => ORDER[status[a.staffId] ?? 'queued'] - ORDER[status[b.staffId] ?? 'queued'] || a.name.localeCompare(b.name)),
  [items, status, filter]);

  if (!open) return null;
  const total = items.length;
  const finished = counts.sent + counts.failed + counts.noemail;
  const failedIds = items.filter((it) => status[it.staffId] === 'failed').map((it) => it.staffId);

  function exportCsv() {
    const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const label: Record<SendState, string> = { sent: 'Sent', failed: 'Failed', noemail: 'No email on file', sending: 'Sending', queued: 'Waiting' };
    const lines = ['Staff,Email,Status,When,Reason', ...rows.map((it) => {
      const s = status[it.staffId] ?? 'queued';
      return [it.name, it.email ?? '', label[s], sentAt[it.staffId] ?? '', reasons[it.staffId] ?? ''].map(esc).join(',');
    })];
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
    a.download = `payslip-emails-${periodLabel.replace(/\s+/g, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const chip = (key: Filter, label: string, value: number, bar: string) => (
    <button
      key={key}
      onClick={() => setFilter(key)}
      aria-pressed={filter === key}
      className={`text-left rounded-r-xl border border-gray-100 dark:border-gray-700/70 border-l-4 ${bar} px-3 py-2 ${filter === key
        ? 'ring-2 ring-[var(--accent-strong)] bg-violet-50 dark:bg-violet-950/30' : 'bg-white dark:bg-gray-800'}`}
    >
      <span className="block text-xl font-bold tabular-nums text-gray-900 dark:text-white">{value}</span>
      <span className="text-[11px] text-gray-500 dark:text-gray-400">{label}</span>
    </button>
  );

  const pill = (it: SendItem) => {
    const s = status[it.staffId] ?? 'queued';
    const base = 'text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap';
    switch (s) {
      case 'sent': return <span className={`${base} bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300`}>✓ Sent {sentAt[it.staffId] ? new Date(sentAt[it.staffId]).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>;
      case 'failed': return <><span className={`${base} bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300`}>✗ Failed</span> <span className="text-xs text-gray-600 dark:text-gray-300">{reasons[it.staffId]}</span></>;
      case 'noemail': return <span className={`${base} bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300`}>No email on file</span>;
      case 'sending': return <span className={`${base} bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300`}>Sending…</span>;
      default: return <span className={`${base} bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300`}>Waiting</span>;
    }
  };

  return (
    <div className={modalBackdrop} onClick={onClose}>
      <div
        className={modalSurface('hr-scope w-full max-w-4xl max-h-[92vh] flex flex-col')}
        onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-labelledby="sm-title"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <div>
            <h2 id="sm-title" className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Mail size={18} className="text-[var(--accent-strong)]" />
              {running ? `Emailing ${periodLabel} payslips` : `${periodLabel} payslips: sending finished`}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {running ? 'You can close this. Sending carries on and the Emailed column fills in.' : 'Each payslip’s result is saved, so you can come back to it later.'}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4 min-h-0">
          <div className="space-y-1">
            <div className="h-2.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={finished}>
              <div className="h-full bg-[var(--accent-strong)] transition-[width] duration-300" style={{ width: `${total ? (finished / total) * 100 : 0}%` }} />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{finished} of {total} done</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {chip('sent', 'Sent', counts.sent, 'border-l-emerald-500')}
            {chip('failed', 'Failed', counts.failed, 'border-l-red-500')}
            {chip('noemail', 'No email on file', counts.noemail, 'border-l-gray-400')}
            {chip('waiting', 'Waiting', counts.waiting, 'border-l-blue-500')}
            {chip('all', 'Everyone', total, 'border-l-[var(--accent-strong)]')}
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-100 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 px-3 font-semibold">Staff</th>
                  <th className="py-2 px-3 font-semibold">Email</th>
                  <th className="py-2 px-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {rows.map((it) => (
                  <tr key={it.staffId}>
                    <td className="py-1.5 px-3 text-gray-800 dark:text-gray-100">{it.name}</td>
                    <td className="py-1.5 px-3 text-gray-500 dark:text-gray-400">{it.email || '—'}</td>
                    <td className="py-1.5 px-3">{pill(it)}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={3} className="py-4 px-3 text-sm text-gray-400">Nobody here.</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-gray-400">“Sent” means the school’s mail server accepted the email. A bounce that comes back later lands in the HR mailbox.</p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex gap-2 flex-wrap">
            <button className={btnGhost} disabled={running || failedIds.length === 0} onClick={() => onRetry(failedIds)}>
              <RotateCcw size={15} /> Retry failed ({failedIds.length})
            </button>
            <button className={btnGhost} onClick={exportCsv} disabled={rows.length === 0}>
              <Download size={15} /> Download list
            </button>
          </div>
          <button className={btnPrimary} onClick={onClose}>{running ? 'Close (keeps sending)' : 'Done'}</button>
        </div>
      </div>
    </div>
  );
}
